import { useEffect, useRef, useState } from "react";

import type { Prospect, ProspectStage } from "../api/hooks";
import { pipelineOrder, useLogTouch } from "../api/hooks";
import { hasNextAction } from "../lib/prospect";

import styles from "./-TouchComposer.module.scss";

// The four ways something happens, in the order they happen in: an email goes
// out, a call follows, a meeting is the good outcome, and a note is the one
// with nobody at the other end.
const channels = ["email", "call", "meeting", "note"] as const;

const channelLabels: Record<(typeof channels)[number], string> = {
  email: "Email",
  call: "Call",
  meeting: "Meeting",
  note: "Note",
};

// What a follow-up preset means, in days. A date picker would be the general
// answer and the wrong one: the composer is filled in between calls, and three
// taps beats a calendar for the two intervals anybody actually picks.
//
// "Leave as is" is the one that matters most and is easiest to leave out: the
// endpoint distinguishes an absent follow-up from a cleared one, and without
// this option every touch would overwrite whatever reminder somebody had
// already set - on the path an operator takes without thinking about it.
const followUps = [
  { value: "keep", label: "Leave as is", days: undefined },
  { value: "3", label: "In 3 days", days: 3 },
  { value: "7", label: "Next week", days: 7 },
  { value: "none", label: "None", days: null },
] as const;

type FollowUp = (typeof followUps)[number]["value"];

// The stage after this one, or nothing when there is nowhere forward to go.
// Advancing is the only move the composer offers: the rest are a decision of
// their own, and the panel's stage picker makes them.
//
// The order comes from the shared list rather than a copy: a stage added there
// would otherwise leave the composer advancing to the one that used to follow.
function nextStage(stage: ProspectStage): ProspectStage | null {
  const at = pipelineOrder.indexOf(stage);
  if (at === -1 || at === pipelineOrder.length - 1) return null;

  return pipelineOrder[at + 1];
}

export function TouchComposer({
  prospect,
  stageLabels,
  onClose,
  onBusyChange,
}: {
  prospect: Prospect;
  stageLabels: Record<ProspectStage, string>;
  onClose: () => void;
  // Reported up because the panel above owns the dialog: a close mid-write
  // unmounts this component and takes the mutation's callbacks with it, so the
  // touch lands and nothing on screen ever says so.
  onBusyChange: (busy: boolean) => void;
}) {
  const [channel, setChannel] = useState<(typeof channels)[number]>("email");
  const [summary, setSummary] = useState("");
  const [note, setNote] = useState("");
  const [advance, setAdvance] = useState(false);
  // Anything already planned is left alone unless someone says otherwise; a
  // prospect with no reminder gets the ordinary one.
  const [followUp, setFollowUp] = useState<FollowUp>(
    hasNextAction(prospect) ? "keep" : "3",
  );
  const [message, setMessage] = useState("");

  const log = useLogTouch();
  const forward = nextStage(prospect.stage);
  const firstChannel = useRef<HTMLButtonElement>(null);

  // Opening the composer unmounts the button that was pressed, so focus falls
  // to <body> inside a dialog claiming aria-modal - the panel's Tab trap then
  // rescues it to the close button, one Enter from throwing away what was about
  // to be typed.
  useEffect(() => {
    firstChannel.current?.focus();
  }, []);

  // Cleared on unmount as well as on every change: a successful save closes
  // the composer in the same batch that clears isPending, so the effect never
  // runs again and the panel would be left believing a write is still in
  // flight - which locks its close button, its Escape key and its scrim for
  // the rest of its life.
  useEffect(() => {
    onBusyChange(log.isPending);

    return () => onBusyChange(false);
  }, [log.isPending, onBusyChange]);

  function save() {
    setMessage("");

    if (summary.trim() === "") {
      // Said here rather than sent and refused: the API enforces it too, and a
      // round trip to be told to say what happened is a round trip.
      setMessage("Say what happened.");
      return;
    }

    const days = followUps.find((option) => option.value === followUp)?.days;

    log.mutate(
      {
        prospectId: prospect.id,
        channel,
        summary,
        note,
        // Absent leaves the prospect where it is. Only advancing is offered, so
        // there is nothing to send when the operator did not ask for it.
        stage: advance && forward !== null ? forward : undefined,
        // Three states. Undefined leaves whatever is planned alone, an empty
        // action with no date clears it, and a preset replaces it.
        //
        // The action text is not asked for: the presets say when, and every
        // follow-up the composer creates is the same thing - go back to them.
        ...(days === undefined
          ? {}
          : {
              nextAction: days === null ? "" : "Follow up",
              nextActionAt:
                days === null
                  ? null
                  : new Date(Date.now() + days * 86_400_000).toISOString(),
            }),
      },
      {
        onSuccess: onClose,
        onError: () => setMessage("That could not be saved. Try again."),
      },
    );
  }

  return (
    <div className={styles.composer}>
      <div className={styles.head}>
        <span className={styles.headTitle}>Log a touch</span>
        {/* No aria-label: one would replace the visible word, and a control
            whose accessible name does not contain what is written on it cannot
            be spoken to. */}
        <button
          type="button"
          className={styles.dismiss}
          disabled={log.isPending}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>

      <div className={styles.channels} role="group" aria-label="How it happened">
        {channels.map((value, index) => (
          <button
            key={value}
            type="button"
            ref={index === 0 ? firstChannel : undefined}
            className={styles.channel}
            aria-pressed={channel === value}
            disabled={log.isPending}
            onClick={() => setChannel(value)}
          >
            {channelLabels[value]}
          </button>
        ))}
      </div>

      <input
        type="text"
        className={styles.summary}
        aria-label="What happened"
        placeholder="What happened"
        value={summary}
        disabled={log.isPending}
        onChange={(event) => setSummary(event.target.value)}
      />

      <textarea
        className={styles.note}
        aria-label="Anything worth keeping"
        placeholder="Anything worth keeping (optional)"
        rows={3}
        value={note}
        disabled={log.isPending}
        onChange={(event) => setNote(event.target.value)}
      />

      {/* Only when there is somewhere forward to go: a prospect that has signed
          up has no next stage, and an "Advance to undefined" is worse than no
          control at all. */}
      {forward !== null && (
        <>
          <div className={styles.label}>Stage</div>
          <div className={styles.options} role="group" aria-label="Stage">
            <button
              type="button"
              className={styles.option}
              aria-pressed={!advance}
              disabled={log.isPending}
              onClick={() => setAdvance(false)}
            >
              Keep at {stageLabels[prospect.stage]}
            </button>
            <button
              type="button"
              className={styles.option}
              aria-pressed={advance}
              disabled={log.isPending}
              onClick={() => setAdvance(true)}
            >
              Advance to {stageLabels[forward]}
            </button>
          </div>
        </>
      )}

      <div className={styles.label}>Follow-up</div>
      <div className={styles.options} role="group" aria-label="Follow-up">
        {followUps.map((option) => (
          <button
            key={option.value}
            type="button"
            className={styles.option}
            aria-pressed={followUp === option.value}
            disabled={log.isPending}
            onClick={() => setFollowUp(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {message !== "" && (
        <div className={styles.error} role="alert">
          {message}
        </div>
      )}

      <button
        type="button"
        className={styles.save}
        disabled={log.isPending}
        onClick={save}
      >
        {log.isPending ? "Saving…" : "Save touch"}
      </button>
    </div>
  );
}
